/**
 * LanguageContext — single source of truth for all translation concerns.
 *
 * Three translation layers:
 *  1. Platform language  — platform homepage shell  → t(key)
 *  2. Restaurant language — customer-facing menu    → tu(key), translateProduct(), translateCategory()
 *  3. Admin panel language — dashboard UI           → ta(key)
 *
 * ══ SINGLE MODE RULE ══════════════════════════════════════════════════════
 * When languageMode === 'single' the restaurant always shows its default
 * language. No translation lookups happen, no language switcher is shown,
 * and the localStorage preference is cleared so it can never bleed across
 * a mode change.
 * ══════════════════════════════════════════════════════════════════════════
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { UI_FA, ADMIN_FA } from '../lang/fa';
import { useUnifiedUi } from '../lang/uiStore';

// ─── Language catalogue ───────────────────────────────────────────────────────
export const LANGUAGES = {
  en: { code: 'en', name: 'English',    flag: '🇬🇧', dir: 'ltr' },
  fa: { code: 'fa', name: 'فارسی',      flag: '🇮🇷', dir: 'rtl' },
  ar: { code: 'ar', name: 'العربية',    flag: '🇸🇦', dir: 'rtl' },
  fr: { code: 'fr', name: 'Français',   flag: '🇫🇷', dir: 'ltr' },
  de: { code: 'de', name: 'Deutsch',    flag: '🇩🇪', dir: 'ltr' },
  tr: { code: 'tr', name: 'Türkçe',     flag: '🇹🇷', dir: 'ltr' },
  es: { code: 'es', name: 'Español',    flag: '🇪🇸', dir: 'ltr' },
  it: { code: 'it', name: 'Italiano',   flag: '🇮🇹', dir: 'ltr' },
  zh: { code: 'zh', name: '中文',        flag: '🇨🇳', dir: 'ltr' },
  ja: { code: 'ja', name: '日本語',      flag: '🇯🇵', dir: 'ltr' },
  ru: { code: 'ru', name: 'Русский',    flag: '🇷🇺', dir: 'ltr' },
  pt: { code: 'pt', name: 'Português',  flag: '🇧🇷', dir: 'ltr' },
};
export const ALL_LANGUAGE_CODES = Object.keys(LANGUAGES);

/**
 * Read a translated entity field, tolerant of both storage shapes:
 *   flat:    translations[lang][entityId][field]      (what the admin saves)
 *   nested:  translations[lang].products[entityId][field] / .categories[...]
 * Returns '' when nothing is found so callers can `|| original`.
 */
function readEntityField(langTrans, entityId, field) {
  if (!langTrans || !entityId) return '';
  const id = String(entityId);
  return (
    langTrans?.[id]?.[field] ||
    langTrans?.products?.[id]?.[field] ||
    langTrans?.categories?.[id]?.[field] ||
    ''
  );
}

// Same lookup but for an array-valued field (translated tags / option labels),
// stored index-aligned with the product's own array.
function readEntityArray(langTrans, entityId, field) {
  if (!langTrans || !entityId) return null;
  const id = String(entityId);
  const v = langTrans?.[id]?.[field] || langTrans?.products?.[id]?.[field];
  return Array.isArray(v) ? v : null;
}

// ─── Platform strings (hardcoded fallbacks for the shell UI) ─────────────────
const PLATFORM_STRINGS = {
  en: {
    home:'Home', menu:'Menu', cart:'Cart', login:'Sign In', register:'Register',
    logout:'Sign Out', profile:'Profile', orders:'My Orders',
    addToCart:'Add to Cart', placeOrder:'Place Order', total:'Total',
    search:'Search…', noItems:'No items found', loading:'Loading…',
    available:'Available', unavailable:'Unavailable',
    features:'Features', pricing:'Pricing', demos:'Live Demos',
    getStarted:'Get Started Free',
  },
  fa: {
    home:'خانه', menu:'منو', cart:'سبد خرید', login:'ورود', register:'ثبت نام',
    logout:'خروج', profile:'پروفایل', orders:'سفارشات من',
    addToCart:'افزودن به سبد', placeOrder:'ثبت سفارش', total:'جمع کل',
    search:'جستجو…', noItems:'آیتمی یافت نشد', loading:'در حال بارگذاری…',
    available:'موجود', unavailable:'ناموجود',
    features:'ویژگی‌ها', pricing:'قیمت‌گذاری', demos:'نمایش زنده',
    getStarted:'شروع رایگان',
  },
  ar: {
    home:'الرئيسية', menu:'القائمة', cart:'السلة', login:'تسجيل الدخول', register:'التسجيل',
    logout:'تسجيل الخروج', profile:'الملف الشخصي', orders:'طلباتي',
    addToCart:'أضف للسلة', placeOrder:'تأكيد الطلب', total:'المجموع',
    search:'بحث…', noItems:'لا توجد عناصر', loading:'جار التحميل…',
    available:'متوفر', unavailable:'غير متوفر',
    features:'الميزات', pricing:'الأسعار', demos:'عروض حية',
    getStarted:'ابدأ مجاناً',
  },
  fr: {
    home:'Accueil', menu:'Menu', cart:'Panier', login:'Connexion', register:"S'inscrire",
    logout:'Déconnexion', profile:'Profil', orders:'Mes commandes',
    addToCart:'Ajouter au panier', placeOrder:'Commander', total:'Total',
    search:'Rechercher…', noItems:'Aucun élément', loading:'Chargement…',
    available:'Disponible', unavailable:'Indisponible',
    features:'Fonctionnalités', pricing:'Tarifs', demos:'Démos en direct',
    getStarted:'Commencer gratuitement',
  },
  de: {
    home:'Startseite', menu:'Speisekarte', cart:'Warenkorb', login:'Anmelden', register:'Registrieren',
    logout:'Abmelden', profile:'Profil', orders:'Meine Bestellungen',
    addToCart:'In den Warenkorb', placeOrder:'Bestellen', total:'Gesamt',
    search:'Suchen…', noItems:'Keine Einträge', loading:'Lädt…',
    available:'Verfügbar', unavailable:'Nicht verfügbar',
    features:'Funktionen', pricing:'Preise', demos:'Live-Demos',
    getStarted:'Kostenlos starten',
  },
};
const getPlatformStrings = (lang) => PLATFORM_STRINGS[lang] || PLATFORM_STRINGS.en;

// ─── Customer UI string keys with English defaults ────────────────────────────
export const UI_SECTIONS = [
  { id:'nav',            label:'🧭 Navigation',             keys:[
    { key:'nav_menu',          default:'Menu'                 },
    { key:'nav_cart',          default:'Cart'                 },
    { key:'nav_back',          default:'Back'                 },
  ]},
  { id:'otp',            label:'📱 Phone login',            keys:[
    { key:'otp_phone_label',   default:'Mobile number'        },
    { key:'otp_send_btn',      default:'Send code'            },
    { key:'otp_code_label',    default:'Verification code'    },
    { key:'otp_verify_btn',    default:'Confirm & continue'   },
    { key:'otp_name_label',    default:'Your name (optional)' },
    { key:'otp_name_ph',       default:'e.g. Sara'            },
    { key:'otp_change_phone',  default:'Change number'        },
    { key:'otp_resend',        default:'Resend code'          },
    { key:'otp_sent_to',       default:'Code sent to'         },
  ]},
  { id:'landing',        label:'🏠 Landing Page',           keys:[
    { key:'landing_view_menu', default:'View Menu →'          },
    { key:'landing_enter_menu',default:'Enter the Menu'       },
    { key:'landing_maps',      default:'Open in Google Maps ↗'},
    { key:'landing_location',  default:'Location'             },
    { key:'landing_phone',     default:'Phone'                },
    { key:'landing_hours',     default:'Hours'                },
    { key:'landing_email',     default:'Email'                },
  ]},
  { id:'menu',           label:'🍽️ Menu Page',             keys:[
    { key:'menu_search',       default:'Search dishes...'     },
    { key:'menu_all',          default:'All'                  },
    { key:'menu_items',        default:'items'                },
    { key:'menu_no_items',     default:'No items found'       },
    { key:'menu_show_original',default:'Show original names'   },
    { key:'menu_original_name',default:'Original'              },
    { key:'menu_results_for',  default:'Results for'           },
    { key:'menu_in_cart',      default:'in cart'               },
    { key:'menu_only_at',      default:'Only at'               },
    { key:'menu_all_branches', default:'All branches'          },
    { key:'menu_pick_branch',  default:'Pick a branch to order from'  },
    { key:'menu_add',          default:'+ Add'                },
    { key:'menu_unavailable',  default:'Unavailable'          },
    { key:'menu_view_cart',    default:'View Cart →'          },
    { key:'menu_related',      default:'You might also like'  },
    { key:'menu_powered_by',   default:'Powered by'           },
    { key:'menu_share',        default:'Share'                },
    { key:'share_whatsapp',    default:'WhatsApp'             },
    { key:'share_telegram',    default:'Telegram'             },
    { key:'share_copy',        default:'Copy link'            },
    { key:'share_copied',      default:'Link copied ✓'        },
    { key:'fav_add',           default:'Add to favorites'     },
    { key:'fav_remove',        default:'Remove from favorites'},
    { key:'profile_favs_tab',  default:'Favorites'            },
    { key:'profile_no_favs',   default:'No favorites yet — tap the ♥ on any dish.' },
    { key:'track_title',       default:'Order Tracking'       },
    { key:'track_order_no',    default:'Order'                },
    { key:'track_status',      default:'Status'               },
    { key:'track_items',       default:'Items'                },
    { key:'track_total',       default:'Total'                },
    { key:'track_placed_at',   default:'Placed at'            },
    { key:'track_auto',        default:'Updates automatically'},
    { key:'track_not_found',   default:'Order not found'      },
    { key:'track_cta',         default:'Track your order →'   },
  ]},
  { id:'cart',           label:'🛒 Cart Page',              keys:[
    { key:'cart_title',        default:'Your Cart'            },
    { key:'cart_clear',        default:'Clear all'            },
    { key:'cart_empty',        default:'Your cart is empty'   },
    { key:'cart_browse',       default:'Browse Menu'          },
    { key:'cart_summary',      default:'Order Summary'        },
    { key:'cart_subtotal',     default:'Subtotal'             },
    { key:'cart_service_fee',  default:'Service fee (5%)'     },
    { key:'cart_total',        default:'Total'                },
    { key:'cart_place_order',  default:'Place Order'          },
    { key:'cart_back',         default:'← Menu'               },
  ]},
  { id:'order_confirm',  label:'📦 Order Confirm Modal',    keys:[
    { key:'confirm_title',     default:'Confirm your order'   },
    { key:'confirm_name',      default:'Name'                 },
    { key:'confirm_phone',     default:'Phone'                },
    { key:'confirm_address',   default:'Address'              },
    { key:'confirm_note',      default:'Your order will be delivered to the address above. Is everything correct?' },
    { key:'confirm_edit',      default:'Edit Details'         },
    { key:'confirm_yes',       default:'Yes, Place Order ✓'   },
  ]},
  { id:'order_success',  label:'✅ Order Success',          keys:[
    { key:'success_title',     default:'Order Placed!'        },
    { key:'success_msg',       default:'Your order has been received' },
    { key:'success_delivering',default:'Delivering to'        },
    { key:'success_contact',   default:'Contact'              },
    { key:'success_continue',  default:'Continue Ordering'    },
    { key:'success_home',      default:'Back to Home'         },
  ]},
  { id:'auth',           label:'🔐 Login & Register',       keys:[
    { key:'auth_signin_title', default:'Sign in to place your order' },
    { key:'auth_signin_sub',   default:'You need an account to track your orders and save your delivery details.' },
    { key:'auth_signin',       default:'Sign In'              },
    { key:'auth_register',     default:'Register'             },
    { key:'auth_name',         default:'Full Name'            },
    { key:'auth_email',        default:'Email'                },
    { key:'auth_password',     default:'Password'             },
    { key:'auth_phone',        default:'Phone Number'         },
    { key:'auth_address',      default:'Delivery Address'     },
    { key:'auth_create',       default:'Create Account'       },
  ]},
  { id:'profile',        label:'👤 Customer Profile',       keys:[
    { key:'profile_tab',       default:'Profile'              },
    { key:'profile_orders_tab',default:'My Orders'            },
    { key:'profile_save',      default:'Save Changes'         },
    { key:'profile_logout',    default:'Sign Out'             },
    { key:'profile_no_orders', default:'No orders yet.'       },
    { key:'profile_browse',    default:'Browse Menu →'        },
    { key:'profile_phone',     default:'Phone'                },
    { key:'profile_address',   default:'Delivery Address'     },
    { key:'profile_curr_pass', default:'Current Password'     },
    { key:'profile_new_pass',  default:'New Password'         },
  ]},
  { id:'complete_profile', label:'📋 Complete Profile Modal', keys:[
    { key:'cp_title',          default:'Complete your profile' },
    { key:'cp_sub',            default:'We need your phone and address before we can place your order.' },
    { key:'cp_save',           default:'Save & Continue'      },
  ]},
  { id:'order_status',   label:'📋 Order Status Labels',    keys:[
    { key:'status_pending',    default:'Pending'              },
    { key:'status_confirmed',  default:'Confirmed'            },
    { key:'status_preparing',  default:'Preparing'            },
    { key:'status_ready',      default:'Ready'                },
    { key:'status_delivered',  default:'Delivered'            },
    { key:'status_cancelled',  default:'Cancelled'            },
  ]},
];
export const UI_DEFAULTS = {};
UI_SECTIONS.forEach(s => s.keys.forEach(k => { UI_DEFAULTS[k.key] = k.default; }));

// ─── Platform landing (marketing homepage) string keys ────────────────────────
// Used by the platform translation tab AND the SSR platform-home.html
// (data-i18n attributes). Keys here mirror the visible copy on platform-home.
export const PLATFORM_LANDING_SECTIONS = [
  { id:'nav', label:'🧭 Navigation', keys:[
    { key:'pl_nav_features',   default:'Features'            },
    { key:'pl_nav_demos',      default:'Live Demos'          },
    { key:'pl_nav_pricing',    default:'Pricing'             },
    { key:'pl_nav_signin',     default:'Sign In'             },
    { key:'pl_nav_get_started',default:'Get Started Free'    },
  ]},
  { id:'hero', label:'🌟 Hero', keys:[
    { key:'pl_hero_badge',     default:'The all-in-one digital menu platform' },
    { key:'pl_hero_title',     default:'Your restaurant deserves a beautiful digital menu' },
    { key:'pl_hero_sub',       default:'Launch a stunning menu on your own subdomain in minutes. Manage products, take orders, and grow — all from one dashboard.' },
    { key:'pl_hero_cta',       default:'Get Started Free'    },
    { key:'pl_hero_cta2',      default:'See Live Demos'      },
    { key:'pl_stat_live',      default:'Live restaurants'    },
    { key:'pl_stat_mobile',    default:'Mobile-first'        },
    { key:'pl_stat_setup',     default:'Setup time'          },
  ]},
  { id:'demos', label:'🍽️ Live Demos', keys:[
    { key:'pl_demos_tag',      default:'Live Examples'       },
    { key:'pl_demos_title',    default:'See it in action'    },
    { key:'pl_demos_sub',      default:'These restaurants are powered by Nooshin right now. Click to visit their menus.' },
    { key:'pl_demos_hint',     default:"Click any card to visit that restaurant's live menu." },
  ]},
  { id:'features', label:'✨ Features', keys:[
    { key:'pl_feat_tag',       default:'Why Nooshin'         },
    { key:'pl_feat_title',     default:'Everything your restaurant needs' },
    { key:'pl_feat_1_t',       default:'Instant Subdomain Menu' },
    { key:'pl_feat_1_d',       default:'Get yourname.nowshin.ir live in minutes. Beautiful, mobile-first menu your customers will love.' },
    { key:'pl_feat_2_t',       default:'QR Code Ready'       },
    { key:'pl_feat_2_d',       default:'Generate a QR code that takes guests straight to your digital menu. No app download needed.' },
    { key:'pl_feat_3_t',       default:'Real-Time Orders'    },
    { key:'pl_feat_3_d',       default:'Receive and manage orders the moment they come in. Update status from Pending to Delivered.' },
    { key:'pl_feat_4_t',       default:'Live Menu Editor'    },
    { key:'pl_feat_4_d',       default:'Add, edit, or 86 any item instantly from your admin panel. Changes go live in seconds.' },
    { key:'pl_feat_5_t',       default:'Analytics Dashboard' },
    { key:'pl_feat_5_d',       default:'Track orders, revenue, and customer behaviour with a rich analytics and AI-insights dashboard.' },
    { key:'pl_feat_6_t',       default:'Three-Layer Auth'    },
    { key:'pl_feat_6_d',       default:'Separate logins for customers, restaurant admins, and platform owners. Everyone sees only what they need.' },
  ]},
  { id:'steps', label:'🚀 How it works', keys:[
    { key:'pl_steps_tag',      default:'Getting Started'     },
    { key:'pl_steps_title',    default:'Live in 3 steps'     },
    { key:'pl_step_1_t',       default:'Register your business' },
    { key:'pl_step_1_d',       default:'Create your account and pick your subdomain. We set everything up instantly.' },
    { key:'pl_step_2_t',       default:'Build your menu'     },
    { key:'pl_step_2_d',       default:'Add categories, products, photos, prices, and tags from your admin panel. No technical skills needed.' },
    { key:'pl_step_3_t',       default:'Go live & take orders' },
    { key:'pl_step_3_d',       default:'Share your QR code or URL. Customers browse, order, and you manage everything from your dashboard.' },
  ]},
  { id:'pricing', label:'💎 Pricing', keys:[
    { key:'pl_price_tag',      default:'Pricing'             },
    { key:'pl_price_title',    default:'Simple, transparent pricing' },
    { key:'pl_price_sub',      default:'Start free for 14 days. No credit card required.' },
    { key:'pl_price_basic',    default:'Basic'               },
    { key:'pl_price_pro',      default:'Pro'                 },
    { key:'pl_price_ent',      default:'Enterprise'          },
    { key:'pl_price_popular',  default:'Most Popular'        },
    { key:'pl_price_choose',   default:'Choose Plan'         },
    { key:'pl_price_get_started', default:'Get Started →'    },
    { key:'pl_price_mo',       default:'/mo'                 },
    { key:'pl_price_b1',       default:'1 restaurant menu'   },
    { key:'pl_price_b2',       default:'Up to 50 products'   },
    { key:'pl_price_b3',       default:'QR code generation'  },
    { key:'pl_price_b4',       default:'Basic analytics'     },
    { key:'pl_price_b5',       default:'Email support'       },
    { key:'pl_price_p1',       default:'Unlimited menus'     },
    { key:'pl_price_p2',       default:'Unlimited products'  },
    { key:'pl_price_p3',       default:'Real-time order management' },
    { key:'pl_price_p4',       default:'Advanced analytics'  },
    { key:'pl_price_p5',       default:'Priority support'    },
    { key:'pl_price_p6',       default:'Custom domain'       },
    { key:'pl_price_e1',       default:'Everything in Pro'   },
    { key:'pl_price_e2',       default:'White-label solution'},
    { key:'pl_price_e3',       default:'API access'          },
    { key:'pl_price_e4',       default:'Dedicated account manager' },
    { key:'pl_price_e5',       default:'SLA guarantee'       },
  ]},
  { id:'cta', label:'📣 Call to action', keys:[
    { key:'pl_cta_title',      default:'Ready to go digital?' },
    { key:'pl_cta_sub',        default:'Join hundreds of restaurants already using Nooshin.' },
    { key:'pl_cta_btn',        default:'Get Started Free'    },
  ]},
  { id:'footer', label:'📄 Footer', keys:[
    { key:'pl_foot_tagline',   default:'The all-in-one digital menu platform for restaurants & cafes.' },
    { key:'pl_foot_for_rest',  default:'For Restaurants'     },
    { key:'pl_foot_signin',    default:'Sign In'             },
    { key:'pl_foot_admin',     default:'Platform Admin'      },
  ]},
];
export const PLATFORM_LANDING_DEFAULTS = {};
PLATFORM_LANDING_SECTIONS.forEach(s => s.keys.forEach(k => { PLATFORM_LANDING_DEFAULTS[k.key] = k.default; }));


// ─── Admin panel UI string keys ───────────────────────────────────────────────
export const ADMIN_UI_SECTIONS = [
  { id:'nav',       label:'🧭 Navigation / Tabs', keys:[
    { key:'adm_overview',      default:'Overview'          },
    { key:'adm_products',      default:'Products'          },
    { key:'adm_categories',    default:'Categories'        },
    { key:'adm_orders',        default:'Orders'            },
    { key:'adm_customers',     default:'Customers'         },
    { key:'adm_admins',        default:'Admins'            },
    { key:'adm_settings',      default:'Settings'          },
    { key:'adm_language',      default:'Language'          },
    { key:'adm_admin_lang',    default:'Admin Language'    },
    { key:'adm_logout',        default:'Sign Out'          },
  ]},
  { id:'orders',    label:'📋 Orders', keys:[
    { key:'adm_ord_title',         default:'Orders'            },
    { key:'adm_ord_refresh',       default:'↻ Refresh'         },
    { key:'adm_ord_empty',         default:'No orders yet.'    },
    { key:'adm_ord_customer',      default:'Customer'          },
    { key:'adm_ord_phone',         default:'Phone'             },
    { key:'adm_ord_address',       default:'Address'           },
    { key:'adm_ord_note',          default:'Note'              },
    { key:'adm_ord_total',         default:'Total'             },
    { key:'adm_status_pending',    default:'Pending'           },
    { key:'adm_status_confirmed',  default:'Confirmed'         },
    { key:'adm_status_preparing',  default:'Preparing'         },
    { key:'adm_status_ready',      default:'Ready'             },
    { key:'adm_status_delivered',  default:'Delivered'         },
    { key:'adm_status_cancelled',  default:'Cancelled'         },
  ]},
  { id:'products',  label:'🍽️ Products', keys:[
    { key:'adm_prod_add',          default:'+ Add Product'     },
    { key:'adm_prod_edit',         default:'Edit'              },
    { key:'adm_prod_delete',       default:'Delete'            },
    { key:'adm_prod_name',         default:'Name'              },
    { key:'adm_prod_price',        default:'Price'             },
    { key:'adm_prod_category',     default:'Category'          },
    { key:'adm_prod_description',  default:'Description'       },
    { key:'adm_prod_save',         default:'Save'              },
    { key:'adm_prod_cancel',       default:'Cancel'            },
    { key:'adm_prod_available',    default:'Available'         },
    { key:'adm_prod_unavailable',  default:'Mark Unavailable'  },
  ]},
  { id:'customers', label:'👥 Customers', keys:[
    { key:'adm_cust_title',        default:'Customers'         },
    { key:'adm_cust_edit',         default:'Edit'              },
    { key:'adm_cust_save',         default:'Save'              },
    { key:'adm_cust_cancel',       default:'Cancel'            },
    { key:'adm_cust_name',         default:'Name'              },
    { key:'adm_cust_phone',        default:'Phone'             },
    { key:'adm_cust_address',      default:'Address'           },
    { key:'adm_cust_activate',     default:'Activate'          },
    { key:'adm_cust_deactivate',   default:'Deactivate'        },
  ]},
  { id:'settings',  label:'⚙️ Settings', keys:[
    { key:'adm_set_save',          default:'Save Settings'     },
    { key:'adm_set_name',          default:'Restaurant Name'   },
    { key:'adm_set_address',       default:'Address'           },
    { key:'adm_set_phone',         default:'Phone'             },
    { key:'adm_set_hours',         default:'Hours'             },
    { key:'adm_set_email',         default:'Email'             },
    { key:'adm_set_cuisine',       default:'Cuisine'           },
    { key:'adm_set_tagline',       default:'Tagline'           },
    { key:'adm_set_description',   default:'Description'       },
  ]},
  { id:'misc', label:'✨ Other labels', keys:[
    { key:'adm_admin_panel',       default:'Admin Panel'       },
    { key:'adm_restaurant_info',   default:'Restaurant Info'   },
    { key:'adm_quick_actions',     default:'Quick Actions'     },
    { key:'adm_click_change_files',default:'Click to change files' },
    { key:'adm_click_upload_menu', default:'Click to upload menu photos or PDF' },
    { key:'adm_import_from_files', default:'Photo / PDF'        },
    { key:'adm_import_from_url',   default:'Website URL'        },
    { key:'adm_import_url_title',  default:'Import from an existing online menu' },
    { key:'adm_import_url_sub',    default:'Paste the link to your current online menu — AI will scan the page and extract every item and category automatically.' },
    { key:'adm_import_url_ph',     default:'https://your-menu-site.com'        },
    { key:'adm_scan_menu_ai',      default:'Scan Menu with AI'  },
    { key:'adm_scanning_url',      default:'Scanning the page and extracting items — this may take 20–40 seconds…' },
    { key:'adm_onb_title',         default:'Getting Started'   },
    { key:'adm_onb_sub',           default:'Complete these steps to launch your menu like a pro.' },
    { key:'adm_onb_logo',          default:'Add your logo'     },
    { key:'adm_onb_products',      default:'Add at least 5 products' },
    { key:'adm_onb_categories',    default:'Create 2+ categories' },
    { key:'adm_onb_info',          default:'Fill in address & hours' },
    { key:'adm_onb_qr',            default:'Download / print your QR code' },
    { key:'adm_onb_done',          default:'All set — your menu is ready! 🎉' },
    { key:'adm_qr_title',          default:'Menu QR Code'      },
    { key:'adm_qr_sub',            default:'Print it on tables and flyers — customers scan it and your menu opens instantly. No app needed.' },
    { key:'adm_qr_download',       default:'Download PNG'      },
    { key:'adm_qr_print',          default:'Print Table Card'  },
    { key:'adm_qr_scan_hint',      default:'Scan to view our menu' },
    { key:'adm_qr_main',           default:'Main Menu'         },
    { key:'adm_options',           default:'Option Buttons'    },
    { key:'adm_options_hint',      default:'Add custom buttons shown on this item (e.g. Spicy 🌶️, Vegan 🌱).' },
    { key:'adm_option_label_ph',   default:'Label e.g. Spicy'  },
    { key:'adm_option_icon_ph',    default:'Icon'              },
    { key:'adm_add_option',        default:'+ Add option'      },
    { key:'adm_select_all',        default:'Select All'        },
    { key:'adm_none',              default:'None'              },
    { key:'adm_item_name',         default:'Item Name'         },
    { key:'adm_price',             default:'Price'             },
    { key:'adm_category',          default:'Category'          },
    { key:'adm_description',       default:'Description'       },
    { key:'adm_description_opt',   default:'Description (optional)' },
    { key:'adm_bulk_price',        default:'Bulk Price Adjustment' },
    { key:'adm_newest_first',      default:'Newest first'      },
    { key:'adm_oldest_first',      default:'Oldest first'      },
    { key:'adm_highest_value',     default:'Highest value'     },
    { key:'adm_auto_refresh',      default:'Auto-refresh'      },
    { key:'adm_no_orders',         default:'No orders found.'  },
    { key:'adm_analytics_ai',      default:'Analytics & AI Insights' },
    { key:'adm_no_customers',      default:'No customers yet.' },
    { key:'adm_address',           default:'Address'           },
    { key:'adm_cover_image',       default:'Cover Image'       },
    { key:'adm_logo',              default:'Logo'              },
    { key:'adm_favicon',           default:'Favicon (browser tab icon)' },
    { key:'adm_currency',          default:'Currency'          },
    { key:'adm_primary_color',     default:'Primary Color'     },
    { key:'adm_accent_color',      default:'Accent Color'      },
    { key:'adm_appearance_sub',    default:'Choose how your customer-facing menu looks. Changes apply instantly after saving.' },
    { key:'adm_design_template',   default:'Design template'   },
    { key:'adm_category_bar',      default:'Category bar'      },
    { key:'adm_base_mode',         default:'Base mode'         },
    { key:'adm_colors',            default:'Colors'            },
    { key:'adm_live_preview',      default:'Live preview'      },
    { key:'adm_save_settings',     default:'Save Settings'     },
    { key:'adm_ai_blog',           default:'AI SEO Blog'       },
    { key:'adm_ai_blog_hint',      default:'Auto-publish a daily multilingual article for your restaurant to boost search ranking (available on select plans)' },
    { key:'adm_on',                default:'On'                },
    { key:'adm_off',               default:'Off'               },
    { key:'adm_lang_mode',         default:'Language Mode'     },
    { key:'adm_default_lang',      default:'Default Language'  },
    { key:'adm_enabled_langs',     default:'Enabled Languages' },
    { key:'adm_translations',      default:'Translations'      },
    { key:'adm_page_title',        default:'Page Title'        },
    { key:'adm_meta_desc',         default:'Meta Description'  },
    { key:'adm_keywords',          default:'Keywords'          },
    { key:'adm_ui_trans_desc',     default:'Translate buttons, labels, and messages customers see in the cart, login, profile, and order flows.' },
    { key:'adm_event_select',      default:'Select an event to configure a popup, discount, and AI-generated banner for your customers.' },
    { key:'adm_live',              default:'LIVE'              },
    { key:'adm_saved',             default:'Saved'             },
    { key:'adm_event_config',      default:'Configure popup, banner, and offers for this event' },
    { key:'adm_title_in_popup',    default:'Title shown in popup' },
    { key:'adm_message',           default:'Message'           },
    { key:'adm_start',             default:'Start'             },
    { key:'adm_end',               default:'End'               },
    { key:'adm_popup_daterange',   default:'Popup appears to customers only within this date range.' },
    { key:'adm_type',              default:'Type'              },
    { key:'adm_no_discount',       default:'No discount'       },
    { key:'adm_fixed_amount',      default:'Fixed amount'      },
    { key:'adm_value',             default:'Value'             },
    { key:'adm_promo_code',        default:'Promo Code (optional)' },
    { key:'adm_featured_badge',    default:'Selected items appear in the popup with a special badge.' },
    { key:'adm_paste_image_url',   default:'Or paste image URL' },
    { key:'adm_remove_event',      default:'Remove Event'      },
    { key:'adm_admin_panel_lang',  default:'Admin Panel Language' },
    { key:'adm_admin_lang_desc',   default:'Translates the labels, tabs and buttons inside this dashboard only. Your customers never see these strings.' },
    { key:'adm_choose_lang',       default:'Choose Language'   },
    { key:'adm_sub_category',      default:'Sub-Category'      },
    { key:'adm_product_image',     default:'Product Image'     },
    { key:'adm_tags',              default:'Tags'              },
    { key:'adm_availability',      default:'Availability'      },
    { key:'adm_cancel',            default:'Cancel'            },
    { key:'adm_slug_url',          default:'Slug (URL key)'    },
    { key:'adm_icon',              default:'Icon'              },
    { key:'adm_item',              default:'Item'              },
    { key:'adm_qty',               default:'Qty'               },
    { key:'adm_thank_you',         default:'Thank you for your order!' },
    { key:'adm_date',              default:'Date'              },
    { key:'adm_not_set',           default:'Not set'           },
    { key:'adm_new_subadmin',      default:'New Sub-Admin'     },
    { key:'adm_access_level',      default:'Access Level'      },
    { key:'adm_no_subadmins',      default:'No sub-admins yet. Add one above.' },
    { key:'adm_visitors',          default:'Visitors'          },
    { key:'adm_visitors_hint',     default:'Real visits to your menu, with bots separated from real people.' },
    { key:'adm_name',              default:'Name'              },
    { key:'adm_email',             default:'Email'             },
    { key:'adm_password',          default:'Password'          },
    { key:'adm_username',          default:'Username'          },
    { key:'adm_username_optional', default:'optional — defaults to email' },
    { key:'adm_ph_item_name',      default:'Item name'         },
    { key:'adm_ph_describe_rest',  default:'Describe your restaurant in 1-2 sentences. What makes it special?' },
    { key:'adm_keywords_hint',     default:'Comma-separated. Less important than title/description but helps with niche queries.' },
    { key:'adm_ph_search_dishes',  default:'Search dishes…'    },
  ]},
  { id:'toasts', label:'🔔 Messages', keys:[
    { key:'adm_t_admin_removed',   default:'Admin removed'        },
    { key:'adm_t_admin_updated',   default:'Admin updated ✓'      },
    { key:'adm_t_availability',    default:'Availability updated' },
    { key:'adm_t_banner',          default:'Banner generated & uploaded ✓' },
    { key:'adm_t_cat_added',       default:'Category added ✓'     },
    { key:'adm_t_cat_deleted',     default:'Category deleted'     },
    { key:'adm_t_cat_required',    default:'Category is required' },
    { key:'adm_t_cat_updated',     default:'Category updated ✓'   },
    { key:'adm_t_cust_updated',    default:'Customer updated ✓'   },
    { key:'adm_t_enable_one_lang', default:'Enable at least one language besides the default first' },
    { key:'adm_t_valid_pct',       default:'Enter a valid percentage (1–1000)' },
    { key:'adm_t_event_removed',   default:'Event removed'        },
    { key:'adm_t_event_saved',     default:'Event saved ✓'        },
    { key:'adm_t_invalid_json',    default:'Invalid JSON file'    },
    { key:'adm_t_lang_saved',      default:'Language settings saved ✓' },
    { key:'adm_t_name_required',   default:'Name is required'     },
    { key:'adm_t_order_updated',   default:'Order updated'        },
    { key:'adm_t_prod_added',      default:'Product added ✓'      },
    { key:'adm_t_prod_deleted',    default:'Product deleted'      },
    { key:'adm_t_prod_updated',    default:'Product updated ✓'    },
    { key:'adm_t_reset_defaults',  default:'Reset to English defaults ✓' },
    { key:'adm_t_reset_en',        default:'Reset to English ✓'   },
    { key:'adm_t_select_item',     default:'Select at least one item to import' },
    { key:'adm_t_settings_saved',  default:'Settings saved ✓'     },
    { key:'adm_t_subadmin_created',default:'Sub-admin created ✓'  },
    { key:'adm_t_switch_multi',    default:'Switch to Multi-language mode and enable some languages first' },
    { key:'adm_t_trans_cleared',   default:'Translations cleared' },
    { key:'adm_t_trans_imported',  default:'Translations imported ✓ — remember to Save' },
    { key:'adm_t_valid_price',     default:'Valid price is required' },
    { key:'adm_t_done_errors',     default:'Done with errors on:' },
    { key:'adm_t_imported',        default:'products imported successfully!' },
    { key:'adm_t_clean_into',        default:'Normalize / clean all content into this language' },
    { key:'adm_t_translated_into', default:'Auto-translated content + UI strings ✓' },
    { key:'adm_t_translated_saved',default:'Translated & saved ✓' },
    { key:'adm_t_translated_ui',   default:'Auto-translated UI strings ✓' },
    { key:'adm_t_admin_translated',default:'Admin panel translated ✓' },
    { key:'adm_t_prices_adjusted', default:'All prices adjusted ✓' },
    { key:'adm_c_remove_event',     default:'Remove this event?' },
    { key:'adm_c_delete_product',   default:'Delete this product? This cannot be undone.' },
    { key:'adm_c_delete_category',  default:'Delete this category and all its products? This cannot be undone.' },
    { key:'adm_c_remove_admin',     default:'Remove this admin account? This cannot be undone.' },
    { key:'adm_c_clear_admin_trans',default:'Clear all admin panel translations?' },
    { key:'adm_c_bulk_price',       default:'Adjust all product prices by this percentage? This will save immediately.' },
    { key:'adm_c_server_translate', default:'Translate the entire menu into every enabled language on the server and save? Existing translations are kept — only missing fields are filled.' },
    { key:'adm_ph_describe_dish',   default:'Describe the dish…' },
    { key:'adm_ph_cat_desc',        default:'Short description for this category' },
    { key:'adm_t_auto_translate',   default:'Auto-Translate' },
    { key:'adm_t_translating',      default:'Translating…' },
    { key:'adm_t_translating_all',  default:'Translating all…' },
    { key:'adm_t_translate_all_save',default:'Translate All Languages & Save' },
    { key:'adm_t_auto_title',       default:'Translate everything into this language (runs in this browser)' },
    { key:'adm_t_all_title',        default:'Translate the whole menu into every enabled language on the server and save — keeps running even if you close this tab' },
    { key:'adm_t_method_note',      default:'Auto-Translate fills the selected language in your browser. Translate All Languages & Save runs entirely on the server — it translates every enabled language at once, keeps existing translations, and saves immediately (you can safely close this tab). Translation uses the AI model plus free providers (MyMemory → Lingva → LibreTranslate); review results before publishing — machine translation can be imperfect.' },
    // Branch selector bar + "make all branches identical" sync tool
    { key:'adm_branch_editing',     default:'Editing menu for:' },
    { key:'adm_branch_main',        default:'Main Restaurant' },
    { key:'adm_branch_note',        default:'Products & categories shown below belong to' },
    { key:'adm_branch_sync',        default:'Make all branches identical' },
    { key:'adm_view_menu',          default:'View Menu' },
    { key:'adm_logout',             default:'Logout' },
    { key:'adm_sync_differ',        default:'Your branches currently have different menus. Choose how to make them identical:' },
    { key:'adm_sync_same',          default:'All branches already share the same menu. You can still re-apply a source below.' },
    { key:'adm_sync_merge',         default:'Merge all — keep every item' },
    { key:'adm_sync_merge_desc',    default:'Combine every branch\'s items into one shared menu, then apply it to all branches and the main restaurant.' },
    { key:'adm_sync_copy',          default:'Copy one menu to all branches' },
    { key:'adm_sync_copy_desc',     default:'Pick a source below; every branch is overwritten to match it exactly.' },
    { key:'adm_sync_source',        default:'Source menu' },
    { key:'adm_sync_apply',         default:'Apply to all branches' },
    { key:'adm_sync_working',       default:'Syncing…' },
    { key:'adm_sync_done',          default:'All branches are now identical ✓' },
    { key:'adm_agg_toggle',         default:'Combined menu' },
    { key:'adm_agg_hint',           default:'When on, the bare domain shows one menu with every food from all branches (de-duplicated, with a tag on items not offered everywhere). Off = visitors must pick a branch.' },
    { key:'adm_agg_saved',          default:'Combined-menu setting saved ✓' },
    { key:'adm_branch_orders',      default:'Orders for:' },
    { key:'adm_branch_all_orders',  default:'All branches' },
    { key:'adm_subadmin_branches',  default:'Branch access' },
    { key:'adm_subadmin_branches_hint', default:'Leave all unchecked for full access to every branch. Check specific branches to limit this admin to only those.' },
  ]},
];
export const ADMIN_UI_DEFAULTS = {};
ADMIN_UI_SECTIONS.forEach(s => s.keys.forEach(k => { ADMIN_UI_DEFAULTS[k.key] = k.default; }));

// ─── Context ──────────────────────────────────────────────────────────────────
const LanguageContext = createContext(null);

const LS_PLAT  = 'nooshin_platform_lang';
const LS_REST  = 'nooshin_restaurant_lang';
const LS_ORIGNAMES = 'nooshin_show_original_names';

export function LanguageProvider({ children, platformSettings, restaurantSettings }) {
  // ── Derive settings from props (recomputed on every render) ───────────────
  const platDefault  = platformSettings?.defaultLanguage  || 'fa';
  const platMode     = platformSettings?.languageMode     || 'single';
  const platEnabled  = platformSettings?.enabledLanguages || ['fa'];

  const restDefault  = restaurantSettings?.defaultLanguage  || 'fa';
  const restMode     = restaurantSettings?.languageMode     || 'single';
  const restEnabled  = restaurantSettings?.enabledLanguages || [restDefault];
  const restTrans    = restaurantSettings?.translations     || {};

  // ── Admin panel translations — stored as REACT STATE so they update live ──
  // When the admin saves and syncUp fires, restaurantSettings prop changes,
  // and these effects re-sync the state immediately.
  const [adminPanelTrans, setAdminPanelTrans] = useState(
    restaurantSettings?.adminPanelTranslations || {}
  );
  const [adminPanelLang, setAdminPanelLang] = useState(
    restaurantSettings?.adminPanelLang || 'fa'
  );

  // Keep admin panel translations in sync when the parent restaurant prop updates
  useEffect(() => {
    setAdminPanelTrans(restaurantSettings?.adminPanelTranslations || {});
    setAdminPanelLang(restaurantSettings?.adminPanelLang || 'fa');
  }, [restaurantSettings?.adminPanelTranslations, restaurantSettings?.adminPanelLang]);

  // ── Platform language ─────────────────────────────────────────────────────
  const [platformLang, setPlatformLangState] = useState(() => {
    // Priority: ?lang= URL param (so the SSR homepage switcher carries over) →
    // saved preference → platform default. Keeps every React surface (incl. the
    // business login) in sync with the homepage language.
    const urlLang = new URLSearchParams(window.location.search).get('lang');
    if (urlLang && platEnabled.includes(urlLang)) return urlLang;
    const saved = localStorage.getItem(LS_PLAT);
    return (saved && platEnabled.includes(saved)) ? saved : platDefault;
  });

  const setPlatformLang = useCallback((lang) => {
    setPlatformLangState(lang);
    localStorage.setItem(LS_PLAT, lang);
  }, []);

  // When the platform settings arrive after first paint, re-evaluate the chosen
  // language against the (now known) enabled list + ?lang= / saved preference,
  // so single→multi or a saved choice is honored without a manual switch.
  useEffect(() => {
    const urlLang = new URLSearchParams(window.location.search).get('lang');
    const saved   = localStorage.getItem(LS_PLAT);
    const want = (urlLang && platEnabled.includes(urlLang)) ? urlLang
               : (saved && platEnabled.includes(saved)) ? saved
               : null;
    if (want) setPlatformLangState(prev => (prev === want ? prev : want));
  }, [platEnabled.join(',')]);

  // ── Restaurant language ───────────────────────────────────────────────────
  // RULE: single mode → ALWAYS restDefault, never read/write localStorage.
  // multi mode → read localStorage, must be in enabled list.
  const computeRestLang = () => {
    if (restMode !== 'multi') return restDefault;
    // Priority: 1) ?lang= URL param  2) localStorage  3) browser language  4) default
    const urlLang = new URLSearchParams(window.location.search).get('lang');
    if (urlLang && restEnabled.includes(urlLang)) return urlLang;
    const saved = localStorage.getItem(LS_REST);
    if (saved && restEnabled.includes(saved)) return saved;
    // Browser language auto-detection for first-time visitors
    const browserLang = (navigator.language || '').split('-')[0].toLowerCase();
    if (restEnabled.includes(browserLang) && browserLang !== restDefault) return browserLang;
    return restDefault;
  };

  const [restaurantLang, setRestaurantLangState] = useState(computeRestLang);

  // Whether to show a product/category's ORIGINAL name alongside its translation
  // (customer preference, persisted). Only meaningful when a translation is
  // actually being applied (multi mode + non-default language).
  const [showOriginalNames, setShowOriginalNamesState] = useState(() => {
    try { return localStorage.getItem(LS_ORIGNAMES) === '1'; } catch { return false; }
  });
  const setShowOriginalNames = useCallback((v) => {
    setShowOriginalNamesState(prev => {
      const next = typeof v === 'function' ? v(prev) : v;
      try { localStorage.setItem(LS_ORIGNAMES, next ? '1' : '0'); } catch {}
      return next;
    });
  }, []);
  // True when stored entity text is being swapped for a translation right now.
  const isTranslating = restMode === 'multi' && restaurantLang !== restDefault;

  const setRestaurantLang = useCallback((lang) => {
    setRestaurantLangState(lang);
    if (restMode === 'multi') {
      localStorage.setItem(LS_REST, lang);
    }
  }, [restMode]);

  // When mode or default language changes, re-evaluate the active restaurant lang
  useEffect(() => {
    if (restMode !== 'multi') {
      // Single mode: force default, erase any saved preference so it can't bleed back
      localStorage.removeItem(LS_REST);
      setRestaurantLangState(restDefault);
    } else {
      // Multi mode: ensure current lang is still in the enabled list
      setRestaurantLangState(prev => {
        if (restEnabled.includes(prev)) return prev;
        return restDefault;
      });
    }
  }, [restMode, restDefault, restEnabled.join(',')]);

  // Apply RTL/LTR and lang attribute to <html>
  useEffect(() => {
    const lang = LANGUAGES[restaurantLang] || LANGUAGES[platformLang] || LANGUAGES.en;
    document.documentElement.dir  = lang.dir;
    document.documentElement.lang = lang.code;
  }, [restaurantLang, platformLang]);

  // ── Translation functions ─────────────────────────────────────────────────

  /** Platform shell UI string (nav, headers on platform homepage) */
  const t = useCallback((key) => {
    return getPlatformStrings(platformLang)[key] || getPlatformStrings('en')[key] || key;
  }, [platformLang]);

  // Unified UI strings (owner-managed, from /api/i18n) — one source shared with
  // the SSR pages and every other surface. Overlays the in-code base below.
  const unifiedRest  = useUnifiedUi(restaurantLang);
  const unifiedAdmin = useUnifiedUi(adminPanelLang);
  const unifiedPlat  = useUnifiedUi(platformLang);

  /**
   * Platform landing-page string (pl_* keys — the marketing homepage shell).
   * Mirrors the SSR platform-home.html data-i18n system so the React landing
   * page shares ONE translation source. Priority:
   *   1) owner-managed unified store (DB, auto-translated to every language)
   *   2) built-in hand-authored Persian (quality fa out of the box)
   *   3) English defaults from PLATFORM_LANDING_SECTIONS
   */
  const tp = useCallback((key) => {
    if (unifiedPlat[key]) return unifiedPlat[key];
    if (platformLang === 'fa' && UI_FA[key]) return UI_FA[key];
    return PLATFORM_LANDING_DEFAULTS[key] || key;
  }, [unifiedPlat, platformLang]);

  /**
   * Customer-facing UI string (buttons, labels on menu/cart/profile pages).
   * Only applies the restaurant's translated override in multi mode when
   * the visitor has switched to a non-default language.
   */
  const tu = useCallback((key) => {
    // 1) Restaurant's own saved translation — applies on the DEFAULT language
    //    too, so a restaurant whose default is e.g. Persian gets a Persian UI
    //    instead of falling back to English chrome.
    if (restMode === 'multi') {
      const override = restTrans?.[restaurantLang]?.ui?.[key];
      if (override) return override;
    }
    // 2) Owner-managed unified store (DB) for the active language
    if (unifiedRest[key]) return unifiedRest[key];
    // 3) Built-in hand-authored catalogue (quality Persian out of the box)
    if (restaurantLang === 'fa' && UI_FA[key]) return UI_FA[key];
    // 4) English defaults
    return UI_DEFAULTS[key] || key;
  }, [restMode, restaurantLang, restTrans, unifiedRest]);

  /**
   * Translate a stored entity field (product name, category name).
   * Returns empty string (not the fallback) so callers can do:
   *   tr(id, 'name') || product.name
   */
  const tr = useCallback((entityId, field) => {
    // Applies on the default language too: if the owner "normalized into the
    // default" there IS a stored overlay to show; otherwise readEntityField
    // returns '' and the caller falls back to the raw field.
    if (restMode !== 'multi') return '';
    return readEntityField(restTrans?.[restaurantLang], entityId, field) || '';
  }, [restMode, restaurantLang, restTrans]);

  /**
   * Returns a product with name/description replaced by translations.
   * In single mode the original is returned unchanged. In multi mode the
   * overlay applies for EVERY language including the default — so a menu whose
   * default is Persian shows the normalized Persian data (each field falls back
   * to the raw value when no overlay exists).
   */
  const translateProduct = useCallback((product) => {
    if (restMode !== 'multi') return product;
    const langTrans = restTrans?.[restaurantLang];
    const id = product._id || product.id;
    const name        = readEntityField(langTrans, id, 'name')        || product.name;
    const description = readEntityField(langTrans, id, 'description') || product.description;
    // Translate the remaining detail fields (tags, option labels) index-aligned
    // with the product's own arrays, falling back to the original per element.
    const tTags = readEntityArray(langTrans, id, 'tags');
    const tags = Array.isArray(product.tags)
      ? product.tags.map((tg, i) => (tTags && tTags[i]) || tg)
      : product.tags;
    const tOpts = readEntityArray(langTrans, id, 'options');
    const options = Array.isArray(product.options)
      ? product.options.map((op, i) => ({ ...op, label: (tOpts && tOpts[i]) || op.label }))
      : product.options;
    // Preserve the untranslated values so the UI can offer a "show both names"
    // view. _orig.name is only meaningful when it actually differs.
    return { ...product, name, description, tags, options, _orig: { name: product.name, description: product.description } };
  }, [restMode, restaurantLang, restTrans]);

  /**
   * Returns a category with name replaced by translation. In single mode the
   * original is returned unchanged; in multi mode the overlay applies for every
   * language including the default (falling back to the raw name when absent).
   */
  const translateCategory = useCallback((cat) => {
    if (restMode !== 'multi') return cat;
    const id = cat._id || cat.id;
    const name = readEntityField(restTrans?.[restaurantLang], id, 'name') || cat.name;
    return { ...cat, name, _orig: { name: cat.name } };
  }, [restMode, restaurantLang, restTrans]);

  /**
   * Admin panel UI string.
   * Reads from the restaurant's adminPanelTranslations flat map.
   * Stored as React state so it updates immediately after save without a page refresh.
   */
  const ta = useCallback((key) => {
    // Saved per-restaurant strings win; the built-in Persian catalogue covers
    // everything else when the admin panel language is Persian; then English.
    return adminPanelTrans?.[key]
      || unifiedAdmin[key]
      || (adminPanelLang === 'fa' ? ADMIN_FA[key] : '')
      || ADMIN_UI_DEFAULTS[key] || key;
  }, [adminPanelTrans, adminPanelLang, unifiedAdmin]);

  // ── Context value ─────────────────────────────────────────────────────────
  const value = {
    // Platform
    platformLang, setPlatformLang,
    platMode, platEnabled,
    // Restaurant (customer-facing)
    restaurantLang, setRestaurantLang,
    restMode, restEnabled, restDefault,
    // Admin panel
    adminPanelLang, adminPanelTrans,
    // Translation helpers
    t, tp, tu, tr, ta, translateProduct, translateCategory,
    // Show-both-names preference
    showOriginalNames, setShowOriginalNames, isTranslating,
    LANGUAGES,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

/** Safe hook — returns English defaults if used outside a LanguageProvider */
export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) return {
    platformLang: 'fa',   setPlatformLang: () => {},
    platMode: 'single',   platEnabled: ['fa'],
    restaurantLang: 'fa', setRestaurantLang: () => {},
    restMode: 'single',   restEnabled: ['fa'], restDefault: 'fa',
    adminPanelLang: 'fa', adminPanelTrans: {},
    t:  k => k,
    tp: k => PLATFORM_LANDING_DEFAULTS[k] || k,
    tu: k => UI_DEFAULTS[k]       || k,
    tr: ()  => '',
    ta: k => ADMIN_UI_DEFAULTS[k] || k,
    translateProduct:  p => p,
    translateCategory: c => c,
    showOriginalNames: false, setShowOriginalNames: () => {}, isTranslating: false,
    LANGUAGES,
  };
  return ctx;
};
